import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Eraser, Undo2, Trash2, PenTool } from "lucide-react";

const SketchCanvas = forwardRef(function SketchCanvas({ className = "", refImageDataUrl }, ref) {
  const canvasRef = useRef(null);
  const bgRef = useRef(null);
  const drawingRef = useRef(false);
  const historyRef = useRef([]);
  const lastRef = useRef(null);
  const [tool, setTool] = useState("pen"); // pen | eraser
  const [size, setSize] = useState(3);

  useImperativeHandle(ref, () => ({
    getDataUrl: () => canvasRef.current?.toDataURL("image/png"),
    getBase64: () => {
      const url = canvasRef.current?.toDataURL("image/png");
      return url ? url.split(",")[1] : null;
    },
    hasContent: () => historyRef.current.length > 0,
    clear: () => clear(),
  }));

  // Setup canvas sizing (retina)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const prev = canvas.toDataURL();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0a0a0a";
      // restore
      if (prev && historyRef.current.length) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
        img.src = prev;
      }
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    historyRef.current.push(canvasRef.current.toDataURL());
    lastRef.current = getPos(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.globalCompositeOperation = tool === "eraser" ? "destination-out" : "source-over";
    ctx.lineWidth = tool === "eraser" ? size * 4 : size;
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastRef.current = pos;
  };

  const end = () => { drawingRef.current = false; };

  const undo = () => {
    const prev = historyRef.current.pop();
    if (!prev) return;
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const rect = canvas.parentElement.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      ctx.drawImage(img, 0, 0, rect.width, rect.height);
    };
    img.src = prev;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const rect = canvas.parentElement.getBoundingClientRect();
    canvas.getContext("2d").clearRect(0, 0, rect.width, rect.height);
    historyRef.current = [];
  };

  return (
    <div className={`relative w-full h-full bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 grain overflow-hidden ${className}`} data-testid="sketch-canvas-container">
      {refImageDataUrl && (
        <img src={refImageDataUrl} alt="ref" className="absolute inset-0 w-full h-full object-contain opacity-20 pointer-events-none z-0" />
      )}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 canvas-cursor z-10"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid="sketch-canvas"
      />
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 backdrop-blur-xl bg-white/70 dark:bg-zinc-950/60 border border-zinc-200/50 dark:border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.12)] px-2 py-1.5">
        <button
          onClick={() => setTool("pen")}
          className={`p-2 ${tool === "pen" ? "bg-lime-400 text-zinc-950" : "text-zinc-600 dark:text-zinc-300 hover:text-lime-500"}`}
          data-testid="sketch-tool-pen"
          title="Pen"
        ><PenTool className="w-4 h-4" /></button>
        <button
          onClick={() => setTool("eraser")}
          className={`p-2 ${tool === "eraser" ? "bg-lime-400 text-zinc-950" : "text-zinc-600 dark:text-zinc-300 hover:text-lime-500"}`}
          data-testid="sketch-tool-eraser"
          title="Eraser"
        ><Eraser className="w-4 h-4" /></button>
        <div className="w-px h-6 bg-zinc-300 dark:bg-white/10 mx-1" />
        <input
          type="range"
          min={1}
          max={12}
          value={size}
          onChange={(e) => setSize(parseInt(e.target.value))}
          className="accent-lime-500 w-24"
          data-testid="sketch-size-range"
        />
        <div className="w-px h-6 bg-zinc-300 dark:bg-white/10 mx-1" />
        <button onClick={undo} className="p-2 text-zinc-600 dark:text-zinc-300 hover:text-lime-500" data-testid="sketch-undo-btn" title="Undo">
          <Undo2 className="w-4 h-4" />
        </button>
        <button onClick={clear} className="p-2 text-zinc-600 dark:text-zinc-300 hover:text-rose-500" data-testid="sketch-clear-btn" title="Clear">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
});

export default SketchCanvas;
